import React from "react";
import { ISAD_CONFIG, NO_DATA_COLOR } from "../../models/isad";

const TREND_CONFIG = {
  improving: { symbol: "↑", label: "Improving", color: "#22c55e" },
  stable: { symbol: "→", label: "Stable", color: "#9ca3af" },
  degrading: { symbol: "↓", label: "Degrading", color: "#ef4444" },
};

const BINDING_TEXT = {
  physical:
    "Physical infrastructure is limiting this segment. Connectivity improvement alone would not elevate the fused level.",
  ctag: "Connectivity is the binding constraint. Physical condition alone is not the bottleneck.",
};

const SYSTEM_LABEL = {
  ctag: "CTAG",
  physical: "Road condition system",
};

const LevelBadge = ({ level, size = "md" }) => {
  const color = ISAD_CONFIG[level]?.color ?? NO_DATA_COLOR;
  const cls =
    size === "lg"
      ? "w-14 h-14 text-2xl font-black rounded-xl"
      : "w-8 h-8 text-sm font-bold rounded-lg";
  return (
    <div
      className={`flex shrink-0 items-center justify-center text-white ${cls}`}
      style={{ background: color }}
    >
      {level ?? "—"}
    </div>
  );
};

const WhatIsIt = ({ detail }) => {
  const {
    fused_level,
    ctag_level,
    physical_level,
    binding_constraint,
    trend,
    data_partial,
  } = detail;
  const fusedCfg = ISAD_CONFIG[fused_level];
  const trendCfg = TREND_CONFIG[trend];

  return (
    <section className="border-b border-gray-100 p-4 dark:border-white/5">
      <p className="mb-3 text-[10px] font-bold uppercase tracking-widest text-gray-400">
        What is it
      </p>

      {data_partial && (
        <div className="mb-3 flex items-start gap-2 rounded-lg border border-yellow-300 bg-yellow-50 px-3 py-2 text-xs text-yellow-800 dark:border-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-300">
          <span className="shrink-0" aria-hidden="true">
            ⚠️
          </span>
          <span>
            One data source unavailable — classification may be incomplete
          </span>
        </div>
      )}

      <div className="mb-4 flex items-center gap-3">
        <LevelBadge level={fused_level} size="lg" />
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
            Fused ISAD
          </p>
          <p className="text-base font-bold text-navy-700 dark:text-white">
            {fusedCfg?.label ?? "No data"}
          </p>
          {trendCfg && (
            <p
              className="mt-0.5 flex items-center gap-1 text-xs font-semibold"
              style={{ color: trendCfg.color }}
            >
              <span aria-hidden="true">{trendCfg.symbol}</span>
              <span>{trendCfg.label}</span>
            </p>
          )}
        </div>
      </div>
      <div className="mb-3 grid grid-cols-2 gap-2">
        {[
          { key: "ctag", level: ctag_level },
          { key: "physical", level: physical_level },
        ].map(({ key, level }) => {
          const cfg = ISAD_CONFIG[level];
          const isBinding = binding_constraint === key;
          return (
            <div
              key={key}
              className={`rounded-lg border p-2.5 ${
                isBinding
                  ? "border-orange-300 bg-orange-50 dark:border-orange-600 dark:bg-orange-900/15"
                  : "border-gray-200 bg-gray-50 dark:border-white/10 dark:bg-navy-900/40"
              }`}
            >
              <p className="mb-1.5 text-[9px] font-bold uppercase tracking-widest text-gray-400">
                {SYSTEM_LABEL[key]}
              </p>
              <div className="flex items-center gap-2">
                <LevelBadge level={level} />
                <p className="text-xs font-medium text-gray-700 dark:text-gray-300">
                  {cfg?.label ?? "No data"}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      {binding_constraint && BINDING_TEXT[binding_constraint] && (
        <p className="rounded-lg border border-orange-200 bg-orange-50 px-3 py-2 text-xs leading-relaxed text-orange-800 dark:border-orange-700 dark:bg-orange-900/20 dark:text-orange-300">
          {BINDING_TEXT[binding_constraint]}
        </p>
      )}
    </section>
  );
};

export default WhatIsIt;
